import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  Slider,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome as AIIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrackChanges as GoalIcon,
  MenuBook as GuideIcon,
  ExpandMore as ExpandMoreIcon,
  Insights as InsightsIcon,
  Flag as MilestoneIcon,
  Shield as ObstacleIcon,
  Timeline as CheckinIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import PageContainer from '../components/PageContainer';
import useRoadmapStore from '../store/roadmapStore';
import apiService from '../utils/api';
import { createEmptySmartGoal, normalizeSmartGoal } from '../data/models';
import { getSmartGoalGuide } from '../data/smartGoalGuide';
import { exportSmartGoalsToPDF, exportSingleSmartGoalToPDF } from '../utils/smartGoalsPdf';
import {
  SMART_CRITERIA,
  GOAL_CATEGORIES,
  REVIEW_FREQUENCIES,
  scoreSmartGoal,
  scoreColor,
  scoreTextColor,
  scoreBand,
  computeProgress,
  goalHealth,
  daysUntil,
  nextMilestone,
  summarizeGoals
} from '../utils/smartGoals';

// Bright values tint chips and fill bars; the token values keep the labels
// legible once the surface underneath turns white.
const HEALTH_FILL = {
  'not-started': '#94a3b8',
  'on-track': '#22c55e',
  'at-risk': '#fbbf24',
  completed: '#38bdf8'
};

const HEALTH_TEXT = {
  'not-started': 'var(--status-slate)',
  'on-track': 'var(--status-green)',
  'at-risk': 'var(--status-amber)',
  completed: 'var(--status-sky)'
};

const today = () => new Date().toISOString().split('T')[0];

// ─── Small pieces ────────────────────────────────────────────────────────────

function ScoreBadge({ score, size = 56 }) {
  const { t } = useTranslation();
  const color = scoreColor(score);
  const textColor = scoreTextColor(score);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: `conic-gradient(${color} ${score * 3.6}deg, var(--track) 0deg)`,
        position: 'relative'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 4,
          borderRadius: '50%',
          bgcolor: 'var(--surface)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography sx={{ fontSize: size * 0.28, fontWeight: 800, color: textColor, lineHeight: 1 }}>
          {score}
        </Typography>
        <Typography sx={{ fontSize: size * 0.13, color: 'var(--fg-muted)', letterSpacing: 0.5 }}>
          {t('smartGoals.score')}
        </Typography>
      </Box>
    </Box>
  );
}

function SmartMeter({ breakdown, dense = false }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={dense ? 0.75 : 1.25}>
      {SMART_CRITERIA.map((criterion) => {
        const value = breakdown[criterion.key] || 0;
        return (
          <Stack key={criterion.key} direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: 1,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(99,102,241,0.15)',
                border: '1px solid rgba(99,102,241,0.3)',
                color: 'var(--accent-fg)',
                fontSize: '0.72rem',
                fontWeight: 800
              }}
            >
              {criterion.letter}
            </Box>
            {!dense && (
              <Typography sx={{ width: 96, fontSize: '0.78rem', color: 'var(--fg-muted)' }}>
                {t(criterion.labelKey)}
              </Typography>
            )}
            <LinearProgress
              variant="determinate"
              value={value}
              sx={{
                flex: 1,
                height: 6,
                bgcolor: 'var(--track)',
                '& .MuiLinearProgress-bar': { bgcolor: scoreColor(value) }
              }}
            />
            <Typography sx={{ width: 34, textAlign: 'right', fontSize: '0.72rem', color: 'var(--fg-muted)' }}>
              {value}%
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}

function SummaryStrip({ goals }) {
  const { t } = useTranslation();
  const summary = useMemo(() => summarizeGoals(goals), [goals]);

  const tiles = [
    { label: t('smartGoals.summary.goals'), value: summary.count, color: 'var(--accent-fg)' },
    { label: t('smartGoals.summary.avgScore'), value: `${summary.avgScore}%`, color: scoreTextColor(summary.avgScore) },
    { label: t('smartGoals.summary.avgProgress'), value: `${summary.avgProgress}%`, color: 'var(--status-sky)' },
    { label: t('smartGoals.summary.onTrack'), value: summary.onTrack, color: 'var(--status-green)' },
    { label: t('smartGoals.summary.atRisk'), value: summary.atRisk, color: 'var(--status-amber)' },
    { label: t('smartGoals.summary.dueSoon'), value: summary.dueSoon, color: 'var(--status-amber)' }
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
        gap: 1.5,
        mb: 3
      }}
    >
      {tiles.map((tile) => (
        <Paper key={tile.label} sx={{ p: 1.75, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: tile.color, lineHeight: 1.2 }}>
            {tile.value}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            {tile.label}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

function GoalCard({ goal, onEdit, onDelete, onCheckIn, onExport }) {
  const { t } = useTranslation();
  const { total, breakdown } = useMemo(() => scoreSmartGoal(goal), [goal]);
  const progress = computeProgress(goal);
  const health = goalHealth(goal);
  const remaining = daysUntil(goal.time_bound?.due_date);
  const upcoming = nextMilestone(goal);
  const m = goal.measurable || {};

  return (
    <Paper sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <ScoreBadge score={total} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--fg-strong)', lineHeight: 1.3 }}>
            {goal.title || t('smartGoals.untitled')}
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
            <Chip
              size="small"
              label={t(`smartGoals.health.${health}`)}
              sx={{
                height: 22,
                fontSize: '0.68rem',
                bgcolor: `${HEALTH_FILL[health]}22`,
                color: HEALTH_TEXT[health],
                border: `1px solid ${HEALTH_FILL[health]}55`
              }}
            />
            <Chip size="small" label={t(`smartGoals.categories.${goal.category}`, goal.category)} sx={{ height: 22, fontSize: '0.68rem' }} />
            <Chip size="small" label={t(`smartGoals.bands.${scoreBand(total)}`)} sx={{ height: 22, fontSize: '0.68rem' }} variant="outlined" />
          </Stack>
        </Box>
      </Stack>

      {m.metric && (
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'var(--track)' }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            {m.metric}
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.5 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--fg-strong)' }}>
              {m.current || m.baseline || '—'}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'var(--fg-muted)' }}>
              → {m.target || '—'} {m.unit}
            </Typography>
          </Stack>
        </Box>
      )}

      <Box>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'var(--fg-muted)' }}>
            {t('smartGoals.progress')}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--fg-strong)' }}>{progress}%</Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            bgcolor: 'var(--track)',
            '& .MuiLinearProgress-bar': { bgcolor: HEALTH_FILL[health] }
          }}
        />
      </Box>

      <SmartMeter breakdown={breakdown} dense />

      <Stack spacing={0.5}>
        <Typography sx={{ fontSize: '0.72rem', color: 'var(--fg-muted)' }}>
          {goal.time_bound?.due_date
            ? remaining >= 0
              ? t('smartGoals.daysRemaining', { days: remaining, date: goal.time_bound.due_date })
              : t('smartGoals.daysOverdue', { days: Math.abs(remaining) })
            : t('smartGoals.noDueDate')}
        </Typography>
        {upcoming && (
          <Typography sx={{ fontSize: '0.72rem', color: 'var(--fg-muted)' }}>
            {t('smartGoals.nextMilestone')}: {upcoming.title} · {upcoming.date}
          </Typography>
        )}
      </Stack>

      <Stack direction="row" spacing={0.5} sx={{ mt: 'auto', pt: 1 }}>
        <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(goal)}>
          {t('common.edit')}
        </Button>
        <Button size="small" startIcon={<CheckinIcon />} onClick={() => onCheckIn(goal)}>
          {t('smartGoals.checkIn')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={t('smartGoals.exportOne')}>
          <IconButton size="small" onClick={() => onExport(goal)} sx={{ color: 'var(--fg-muted)' }}>
            <PdfIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('common.delete')}>
          <IconButton size="small" onClick={() => onDelete(goal)} sx={{ color: 'var(--fg-muted)', '&:hover': { color: 'var(--status-red)' } }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

// ─── Editor ──────────────────────────────────────────────────────────────────

function FieldBlock({ letter, title, hint, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(99,102,241,0.18)',
            border: '1px solid rgba(99,102,241,0.35)',
            color: 'var(--accent-fg)',
            fontWeight: 800,
            fontSize: '0.8rem'
          }}
        >
          {letter}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--fg-strong)' }}>{title}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'var(--fg-muted)' }}>{hint}</Typography>
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

function GoalEditorDialog({ open, goal, objectives, roadmapId, onSave, onClose, notify }) {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState(goal);
  const [coaching, setCoaching] = useState(false);
  const [critique, setCritique] = useState(null);
  const [newMilestone, setNewMilestone] = useState({ title: '', date: '' });
  const [newObstacle, setNewObstacle] = useState({ risk: '', plan: '' });

  React.useEffect(() => {
    setDraft(goal);
    setCritique(null);
  }, [goal]);

  if (!draft) return null;

  const { total, breakdown } = scoreSmartGoal(draft);

  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const setNested = (key, patch) => setDraft((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const addMilestone = () => {
    if (!newMilestone.title.trim()) return;
    set({ milestones: [...(draft.milestones || []), { ...newMilestone, completed: false }] });
    setNewMilestone({ title: '', date: '' });
  };

  const addObstacle = () => {
    if (!newObstacle.risk.trim()) return;
    set({ obstacles: [...(draft.obstacles || []), { ...newObstacle }] });
    setNewObstacle({ risk: '', plan: '' });
  };

  const runCoach = async () => {
    setCoaching(true);
    setCritique(null);
    try {
      const result = await apiService.coachSmartGoal(draft, roadmapId, i18n.language);
      setCritique(result);
    } catch (error) {
      notify(error.message || t('smartGoals.ai.coachFailed'), 'error');
    } finally {
      setCoaching(false);
    }
  };

  const applyImproved = () => {
    if (!critique?.improved_goal) return;
    setDraft(normalizeSmartGoal({
      ...draft,
      ...critique.improved_goal,
      id: draft.id,
      checkins: draft.checkins,
      status: draft.status,
      progress: draft.progress
    }));
    setCritique(null);
    notify(t('smartGoals.ai.applied'), 'success');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1.5 }}>
        <ScoreBadge score={total} size={46} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
            {draft.title || t('smartGoals.editor.newTitle')}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--fg-muted)' }}>
            {t(`smartGoals.bands.${scoreBand(total)}`)} — {t('smartGoals.editor.liveScoreHint')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 320px' }, gap: 3 }}>
          {/* ── form ── */}
          <Stack spacing={3}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
              <TextField
                label={t('smartGoals.fields.title')}
                value={draft.title}
                onChange={(e) => set({ title: e.target.value })}
                placeholder={t('smartGoals.placeholders.title')}
                fullWidth
                required
              />
              <TextField
                select
                label={t('smartGoals.fields.category')}
                value={draft.category}
                onChange={(e) => set({ category: e.target.value })}
                fullWidth
              >
                {GOAL_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>{t(`smartGoals.categories.${c}`, c)}</MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label={t('smartGoals.fields.owner')}
                value={draft.owner}
                onChange={(e) => set({ owner: e.target.value })}
                placeholder={t('smartGoals.placeholders.owner')}
                fullWidth
              />
              <TextField
                select
                label={t('smartGoals.fields.priority')}
                value={draft.priority}
                onChange={(e) => set({ priority: e.target.value })}
                fullWidth
              >
                <MenuItem value="High">{t('priority.high')}</MenuItem>
                <MenuItem value="Medium">{t('priority.medium')}</MenuItem>
                <MenuItem value="Low">{t('priority.low')}</MenuItem>
              </TextField>
            </Box>

            <Divider />

            <FieldBlock letter="S" title={t('smartGoals.criteria.specific.name')} hint={t('smartGoals.criteria.specific.hint')}>
              <TextField
                multiline
                minRows={3}
                fullWidth
                value={draft.specific}
                onChange={(e) => set({ specific: e.target.value })}
                placeholder={t('smartGoals.placeholders.specific')}
              />
            </FieldBlock>

            <FieldBlock letter="M" title={t('smartGoals.criteria.measurable.name')} hint={t('smartGoals.criteria.measurable.hint')}>
              <Stack spacing={2}>
                <TextField
                  label={t('smartGoals.fields.metric')}
                  value={draft.measurable.metric}
                  onChange={(e) => setNested('measurable', { metric: e.target.value })}
                  placeholder={t('smartGoals.placeholders.metric')}
                  fullWidth
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                  <TextField
                    label={t('smartGoals.fields.baseline')}
                    value={draft.measurable.baseline}
                    onChange={(e) => setNested('measurable', { baseline: e.target.value })}
                  />
                  <TextField
                    label={t('smartGoals.fields.current')}
                    value={draft.measurable.current}
                    onChange={(e) => setNested('measurable', { current: e.target.value })}
                  />
                  <TextField
                    label={t('smartGoals.fields.target')}
                    value={draft.measurable.target}
                    onChange={(e) => setNested('measurable', { target: e.target.value })}
                  />
                  <TextField
                    label={t('smartGoals.fields.unit')}
                    value={draft.measurable.unit}
                    onChange={(e) => setNested('measurable', { unit: e.target.value })}
                    placeholder="%, USD, users"
                  />
                </Box>
              </Stack>
            </FieldBlock>

            <FieldBlock letter="A" title={t('smartGoals.criteria.achievable.name')} hint={t('smartGoals.criteria.achievable.hint')}>
              <TextField
                multiline
                minRows={2}
                fullWidth
                value={draft.achievable}
                onChange={(e) => set({ achievable: e.target.value })}
                placeholder={t('smartGoals.placeholders.achievable')}
              />
            </FieldBlock>

            <FieldBlock letter="R" title={t('smartGoals.criteria.relevant.name')} hint={t('smartGoals.criteria.relevant.hint')}>
              <Stack spacing={2}>
                <TextField
                  multiline
                  minRows={2}
                  fullWidth
                  value={draft.relevant}
                  onChange={(e) => set({ relevant: e.target.value })}
                  placeholder={t('smartGoals.placeholders.relevant')}
                />
                <TextField
                  select
                  label={t('smartGoals.fields.linkedObjective')}
                  value={draft.linked_objective === null || draft.linked_objective === undefined ? '' : draft.linked_objective}
                  onChange={(e) => set({ linked_objective: e.target.value === '' ? null : Number(e.target.value) })}
                  fullWidth
                  helperText={t('smartGoals.fields.linkedObjectiveHelp')}
                >
                  <MenuItem value="">{t('common.none')}</MenuItem>
                  {objectives.map((obj, idx) => (
                    <MenuItem key={idx} value={idx}>{obj.name || `Objective ${idx + 1}`}</MenuItem>
                  ))}
                </TextField>
              </Stack>
            </FieldBlock>

            <FieldBlock letter="T" title={t('smartGoals.criteria.timeBound.name')} hint={t('smartGoals.criteria.timeBound.hint')}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                <TextField
                  type="date"
                  label={t('smartGoals.fields.startDate')}
                  value={draft.time_bound.start_date}
                  onChange={(e) => setNested('time_bound', { start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  type="date"
                  label={t('smartGoals.fields.dueDate')}
                  value={draft.time_bound.due_date}
                  onChange={(e) => setNested('time_bound', { due_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  select
                  label={t('smartGoals.fields.reviewFrequency')}
                  value={draft.time_bound.review_frequency}
                  onChange={(e) => setNested('time_bound', { review_frequency: e.target.value })}
                >
                  {REVIEW_FREQUENCIES.map((f) => (
                    <MenuItem key={f} value={f}>{t(`smartGoals.frequencies.${f}`, f)}</MenuItem>
                  ))}
                </TextField>
              </Box>
            </FieldBlock>

            <Divider />

            {/* Milestones */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <MilestoneIcon fontSize="small" sx={{ color: 'var(--accent-fg)' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('smartGoals.sections.milestones')}</Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.72rem', color: 'var(--fg-muted)', mb: 1.5 }}>
                {t('smartGoals.sections.milestonesHint')}
              </Typography>
              <Stack spacing={1}>
                {(draft.milestones || []).map((ms, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    <Checkbox
                      size="small"
                      checked={!!ms.completed}
                      onChange={(e) => {
                        const updated = [...draft.milestones];
                        updated[idx] = { ...ms, completed: e.target.checked };
                        set({ milestones: updated });
                      }}
                    />
                    <Typography sx={{ flex: 1, fontSize: '0.85rem', textDecoration: ms.completed ? 'line-through' : 'none' }}>
                      {ms.title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--fg-muted)' }}>{ms.date}</Typography>
                    <IconButton size="small" onClick={() => set({ milestones: draft.milestones.filter((_, i) => i !== idx) })}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('smartGoals.fields.milestoneTitle')}
                  value={newMilestone.title}
                  onChange={(e) => setNewMilestone({ ...newMilestone, title: e.target.value })}
                />
                <TextField
                  size="small"
                  type="date"
                  label={t('smartGoals.fields.date')}
                  value={newMilestone.date}
                  onChange={(e) => setNewMilestone({ ...newMilestone, date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                <Button variant="outlined" startIcon={<AddIcon />} onClick={addMilestone}>{t('common.add')}</Button>
              </Stack>
            </Box>

            {/* Obstacles */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <ObstacleIcon fontSize="small" sx={{ color: 'var(--accent-fg)' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('smartGoals.sections.obstacles')}</Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.72rem', color: 'var(--fg-muted)', mb: 1.5 }}>
                {t('smartGoals.sections.obstaclesHint')}
              </Typography>
              <Stack spacing={1}>
                {(draft.obstacles || []).map((ob, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{ob.risk}</Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: 'var(--fg-muted)' }}>{ob.plan}</Typography>
                      </Box>
                      <IconButton size="small" onClick={() => set({ obstacles: draft.obstacles.filter((_, i) => i !== idx) })}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('smartGoals.fields.risk')}
                  value={newObstacle.risk}
                  onChange={(e) => setNewObstacle({ ...newObstacle, risk: e.target.value })}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    label={t('smartGoals.fields.plan')}
                    placeholder={t('smartGoals.placeholders.plan')}
                    value={newObstacle.plan}
                    onChange={(e) => setNewObstacle({ ...newObstacle, plan: e.target.value })}
                  />
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={addObstacle}>{t('common.add')}</Button>
                </Stack>
              </Stack>
            </Box>

            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
                {t('smartGoals.fields.manualProgress')}: {draft.progress}%
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'var(--fg-muted)', mb: 1 }}>
                {t('smartGoals.fields.manualProgressHint')}
              </Typography>
              <Slider
                value={draft.progress}
                onChange={(e, value) => set({ progress: value })}
                min={0}
                max={100}
                step={5}
                valueLabelDisplay="auto"
              />
            </Box>
          </Stack>

          {/* ── live score + AI coach ── */}
          <Stack spacing={2} sx={{ position: { md: 'sticky' }, top: 0, alignSelf: 'start' }}>
            <Paper sx={{ p: 2 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, mb: 1.5, color: 'var(--fg-strong)' }}>
                {t('smartGoals.editor.liveScore')}
              </Typography>
              <SmartMeter breakdown={breakdown} />
            </Paper>

            <Button
              variant="contained"
              startIcon={coaching ? <CircularProgress size={16} color="inherit" /> : <AIIcon />}
              onClick={runCoach}
              disabled={coaching || !draft.title.trim()}
              sx={{ background: 'linear-gradient(135deg, #a855f7, #6366f1)' }}
            >
              {coaching ? t('ai.generating') : t('smartGoals.ai.coach')}
            </Button>

            {critique && (
              <Paper sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                  <ScoreBadge score={Math.round(critique.score || 0)} size={44} />
                  <Typography sx={{ fontSize: '0.8rem', color: 'var(--fg-muted)' }}>
                    {critique.verdict}
                  </Typography>
                </Stack>
                <Stack spacing={1.25}>
                  {(critique.criteria || []).map((c) => (
                    <Box key={c.letter}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: scoreTextColor(c.score || 0) }}>
                        {c.letter} · {c.name} — {c.score}%
                      </Typography>
                      {c.issue && (
                        <Typography sx={{ fontSize: '0.74rem', color: 'var(--fg-muted)' }}>{c.issue}</Typography>
                      )}
                      {c.fix && (
                        <Typography sx={{ fontSize: '0.74rem', color: 'var(--accent-fg)' }}>→ {c.fix}</Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
                {(critique.risks || []).length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 0.5 }}>
                      {t('smartGoals.ai.risks')}
                    </Typography>
                    {critique.risks.map((risk, idx) => (
                      <Typography key={idx} sx={{ fontSize: '0.74rem', color: 'var(--fg-muted)' }}>• {risk}</Typography>
                    ))}
                  </Box>
                )}
                {critique.improved_goal && (
                  <Button fullWidth variant="outlined" sx={{ mt: 2 }} startIcon={<CheckCircleIcon />} onClick={applyImproved}>
                    {t('smartGoals.ai.applyImproved')}
                  </Button>
                )}
              </Paper>
            )}
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!draft.title.trim()} onClick={() => onSave(draft)}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── AI draft dialog ─────────────────────────────────────────────────────────

function AIDraftDialog({ open, onClose, roadmapId, onDrafted, notify }) {
  const { t, i18n } = useTranslation();
  const [idea, setIdea] = useState('');
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const result = await apiService.generateSmartGoal(idea, roadmapId, i18n.language);
      onDrafted(normalizeSmartGoal({ ...createEmptySmartGoal(), ...result }));
      setIdea('');
    } catch (error) {
      notify(error.message || t('smartGoals.ai.generateFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <AIIcon sx={{ color: 'var(--ai-accent)' }} />
        {t('smartGoals.ai.draftTitle')}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '0.85rem', color: 'var(--fg-muted)', mb: 2 }}>
          {t('smartGoals.ai.draftDescription')}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          placeholder={t('smartGoals.ai.draftPlaceholder')}
        />
        <Typography sx={{ fontSize: '0.72rem', color: 'var(--fg-faint)', mt: 1 }}>
          {t('ai.poweredByClaude')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={generate}
          disabled={loading || idea.trim().length < 5}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AIIcon />}
        >
          {loading ? t('ai.generating') : t('smartGoals.ai.draftButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Check-in dialog ─────────────────────────────────────────────────────────

function CheckInDialog({ open, goal, onClose, onSave }) {
  const { t } = useTranslation();
  const [entry, setEntry] = useState({ date: today(), value: '', note: '' });

  React.useEffect(() => {
    if (goal) setEntry({ date: today(), value: goal.measurable?.current || '', note: '' });
  }, [goal]);

  if (!goal) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('smartGoals.checkInTitle')}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '0.85rem', color: 'var(--fg-muted)', mb: 2 }}>
          {goal.measurable?.metric || goal.title}
        </Typography>
        <Stack spacing={2}>
          <TextField
            type="date"
            label={t('smartGoals.fields.date')}
            value={entry.date}
            onChange={(e) => setEntry({ ...entry, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label={`${t('smartGoals.fields.currentValue')} ${goal.measurable?.unit || ''}`}
            value={entry.value}
            onChange={(e) => setEntry({ ...entry, value: e.target.value })}
            fullWidth
          />
          <TextField
            label={t('smartGoals.fields.note')}
            value={entry.note}
            onChange={(e) => setEntry({ ...entry, note: e.target.value })}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
        {(goal.checkins || []).length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 0.5 }}>{t('smartGoals.history')}</Typography>
            {[...goal.checkins]
              .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
              .slice(0, 5)
              .map((c, idx) => (
                <Typography key={idx} sx={{ fontSize: '0.74rem', color: 'var(--fg-muted)' }}>
                  {c.date} · {c.value} {c.note ? `— ${c.note}` : ''}
                </Typography>
              ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={() => onSave(entry)} disabled={!entry.value.trim()}>
          {t('smartGoals.logCheckIn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Guide tab ───────────────────────────────────────────────────────────────

function GuideTab({ onExport }) {
  const { t, i18n } = useTranslation();
  const guide = getSmartGoalGuide(i18n.language);

  return (
    <Box>
      <Paper sx={{ p: { xs: 2.5, md: 3.5 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--fg-strong)' }}>{guide.title}</Typography>
            <Typography sx={{ mt: 1, color: 'var(--fg-muted)', maxWidth: 720 }}>{guide.subtitle}</Typography>
          </Box>
          <Button variant="outlined" startIcon={<PdfIcon />} onClick={onExport} sx={{ flexShrink: 0 }}>
            {t('smartGoals.downloadGuide')}
          </Button>
        </Stack>
      </Paper>

      {guide.sections.map((section, idx) => (
        <Accordion key={section.id} defaultExpanded={idx === 0} sx={{ mb: 1.5 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700, color: 'var(--fg-strong)' }}>{section.heading}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {section.body && (
              <Typography sx={{ color: 'var(--fg-muted)', mb: 2, lineHeight: 1.7 }}>{section.body}</Typography>
            )}
            <Stack spacing={2}>
              {(section.points || []).map((point, i) => (
                <Box key={i} sx={{ pl: 2, borderLeft: '2px solid rgba(99,102,241,0.4)' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--accent-fg)' }}>{point.title}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', color: 'var(--fg-muted)', lineHeight: 1.7 }}>
                    {point.text}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {section.example && (
              <Box sx={{ mt: 2.5, p: 2, borderRadius: 2, bgcolor: 'var(--track)' }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--status-red)', mb: 0.5 }}>
                  {section.example.badLabel}
                </Typography>
                <Typography sx={{ fontSize: '0.88rem', color: 'var(--fg-muted)', fontStyle: 'italic', mb: 2 }}>
                  {section.example.bad}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--status-green)', mb: 0.5 }}>
                  {section.example.goodLabel}
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', color: 'var(--fg-strong)', lineHeight: 1.7 }}>
                  {section.example.good}
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                  {(section.example.notes || []).map((note, i) => (
                    <Typography key={i} sx={{ fontSize: '0.8rem', color: 'var(--fg-muted)' }}>• {note}</Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function SmartGoalsPage() {
  const { t, i18n } = useTranslation();
  const roadmapData = useRoadmapStore((s) => s.roadmapData);
  const currentRoadmapId = useRoadmapStore((s) => s.currentRoadmapId);
  const saveSmartGoal = useRoadmapStore((s) => s.saveSmartGoal);
  const deleteSmartGoal = useRoadmapStore((s) => s.deleteSmartGoal);
  const canEdit = useRoadmapStore((s) => s.canEdit);

  const goals = useMemo(
    () => (roadmapData.smart_goals || []).map(normalizeSmartGoal),
    [roadmapData.smart_goals]
  );
  const objectives = roadmapData.steps?.strategic_objectives?.objectives || [];
  const readOnly = !canEdit();

  const [tab, setTab] = useState(0);
  const [editing, setEditing] = useState(null);
  const [draftOpen, setDraftOpen] = useState(false);
  const [checkInGoal, setCheckInGoal] = useState(null);
  const [toast, setToast] = useState(null);

  const notify = (message, severity = 'info') => setToast({ message, severity });

  const handleSave = (goal) => {
    saveSmartGoal(goal);
    setEditing(null);
    notify(t('smartGoals.saved'), 'success');
  };

  const handleDelete = (goal) => {
    if (window.confirm(t('common.confirmDelete'))) {
      deleteSmartGoal(goal.id);
      notify(t('smartGoals.deleted'), 'info');
    }
  };

  const handleCheckInSave = (entry) => {
    const updated = {
      ...checkInGoal,
      measurable: { ...checkInGoal.measurable, current: entry.value },
      checkins: [...(checkInGoal.checkins || []), entry]
    };
    updated.status = goalHealth(updated);
    saveSmartGoal(updated);
    setCheckInGoal(null);
    notify(t('smartGoals.checkInSaved'), 'success');
  };

  const exportAll = () => {
    exportSmartGoalsToPDF(goals, {
      projectName: roadmapData.project_name,
      language: i18n.language,
      includeGuide: true
    });
  };

  const exportGuideOnly = () => {
    exportSmartGoalsToPDF([], {
      title: getSmartGoalGuide(i18n.language).title,
      projectName: roadmapData.project_name,
      language: i18n.language,
      includeGuide: true
    });
  };

  return (
    <PageContainer maxWidth={1280}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2.5, md: 3.5 }, mb: 3, position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            background: 'linear-gradient(180deg, #6366f1, #a855f7)'
          }}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <GoalIcon sx={{ color: 'var(--accent-fg)' }} />
              <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: 'var(--fg-strong)' }}>
                {t('smartGoals.title')}
              </Typography>
            </Stack>
            <Typography sx={{ mt: 1.5, color: 'var(--fg-muted)', maxWidth: 720, lineHeight: 1.65 }}>
              {t('smartGoals.intro')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexShrink={0}>
            {!readOnly && (
              <Button
                variant="contained"
                startIcon={<AIIcon />}
                onClick={() => setDraftOpen(true)}
                sx={{ background: 'linear-gradient(135deg, #a855f7, #6366f1)' }}
              >
                {t('smartGoals.ai.draftButton')}
              </Button>
            )}
            {!readOnly && (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setEditing(createEmptySmartGoal())}>
                {t('smartGoals.newGoal')}
              </Button>
            )}
            <Tooltip title={t('smartGoals.exportAllTooltip')}>
              <span>
                <Button variant="outlined" startIcon={<PdfIcon />} onClick={exportAll} disabled={goals.length === 0}>
                  {t('actions.exportPDF')}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {!currentRoadmapId && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('smartGoals.noProjectWarning')}
        </Alert>
      )}

      <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{ mb: 3 }}>
        <Tab icon={<InsightsIcon fontSize="small" />} iconPosition="start" label={t('smartGoals.tabs.goals')} />
        <Tab icon={<GuideIcon fontSize="small" />} iconPosition="start" label={t('smartGoals.tabs.guide')} />
      </Tabs>

      {tab === 0 && (
        <Box>
          <SummaryStrip goals={goals} />

          {goals.length === 0 ? (
            <Paper sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
              <GoalIcon sx={{ fontSize: 42, color: 'rgba(99,102,241,0.6)', mb: 1.5 }} />
              <Typography variant="h6" sx={{ color: 'var(--fg-strong)', fontWeight: 700 }}>
                {t('smartGoals.empty.title')}
              </Typography>
              <Typography sx={{ mt: 1, color: 'var(--fg-muted)', maxWidth: 520, mx: 'auto' }}>
                {t('smartGoals.empty.description')}
              </Typography>
              {!readOnly && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<AIIcon />}
                    onClick={() => setDraftOpen(true)}
                    sx={{ background: 'linear-gradient(135deg, #a855f7, #6366f1)' }}
                  >
                    {t('smartGoals.ai.draftButton')}
                  </Button>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setEditing(createEmptySmartGoal())}>
                    {t('smartGoals.newGoal')}
                  </Button>
                  <Button startIcon={<GuideIcon />} onClick={() => setTab(1)}>
                    {t('smartGoals.readGuide')}
                  </Button>
                </Stack>
              )}
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 2.5
              }}
            >
              {goals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onEdit={setEditing}
                  onDelete={handleDelete}
                  onCheckIn={setCheckInGoal}
                  onExport={(g) =>
                    exportSingleSmartGoalToPDF(g, {
                      projectName: roadmapData.project_name,
                      language: i18n.language,
                      includeGuide: false
                    })
                  }
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {tab === 1 && <GuideTab onExport={exportGuideOnly} />}

      <GoalEditorDialog
        open={!!editing}
        goal={editing}
        objectives={objectives}
        roadmapId={currentRoadmapId}
        onSave={handleSave}
        onClose={() => setEditing(null)}
        notify={notify}
      />

      <AIDraftDialog
        open={draftOpen}
        roadmapId={currentRoadmapId}
        onClose={() => setDraftOpen(false)}
        onDrafted={(goal) => {
          setDraftOpen(false);
          setEditing(goal);
          notify(t('smartGoals.ai.drafted'), 'success');
        }}
        notify={notify}
      />

      <CheckInDialog
        open={!!checkInGoal}
        goal={checkInGoal}
        onClose={() => setCheckInGoal(null)}
        onSave={handleCheckInSave}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity || 'info'} onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
